<script lang="ts">
	import type { SbColor, SbImage, SbLink } from '../types';
	import SbRichtext from '../components/sbRichtext.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import { trackEvent } from '$lib/segment/client';
	import { page } from '$app/state';
	import { onDestroy } from 'svelte';
	import Countdown from '../parts/banner/countdown.svelte';
	import { isEmpty } from '$lib/utils/storyblok/richtext';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import { friendbuy } from '$lib/state/friendbuy.svelte';
	import { emailCapture } from '$lib/state/emailCapture.svelte';
	import Image from '../components/image.svelte';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<{
			text: ISbRichtext;
			text_desktop: ISbRichtext;
			leading_icon: SbImage;
			content_cta: ISbStoryData['content'][];
			cta_label: string;
			cta_link: SbLink;
			text_color: SbColor;
			bg_color: SbColor;
			target: SbLink;
			target_type: 'link' | 'telephone' | 'messenger';
			end_date: string;
			countdown_duration: number;
			countdown_active: boolean;
			countdown_text: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let href = $derived(getTarget(getSbUrl(content.target), content.target_type));
	let hasHref = $derived(href !== '#');

	let hasCountdown = $derived(content.countdown_active && content.end_date?.length > 0);

	function handleClick() {
		trackEvent('Site Banner Click', { path: page.url.pathname, url: href });
	}

	function getTarget(target: string, targetType: string): string {
		switch (targetType) {
			case 'telephone':
				return `tel:${target}`;
			case 'messenger':
				return `sms:${target}`;
			case 'link':
			default:
				return target;
		}
	}

	let banner = $state<HTMLElement>();

	function removeBanner({ animationName }: AnimationEvent) {
		if (animationName.endsWith('banner-out')) {
			banner?.remove();
		}
	}

	let shouldHide = $derived(
		friendbuy.show || (href.includes('newsletter=') && emailCapture.successfullyEntered)
	);

	$effect(() => {
		if (!banner || shouldHide) return;

		const bannerContainer = document.getElementById('site-banner');
		if (bannerContainer) {
			bannerContainer.appendChild(banner);
			banner.classList.remove('hidden');
			banner.classList.add('animate-banner-in');
		}
	});

	$effect(() => {
		if (banner && shouldHide) {
			banner.addEventListener('animationend', removeBanner);
			banner.classList.remove('animate-banner-in');
			banner.classList.add('animate-banner-out');
		}
	});

	onDestroy(() => {
		banner?.remove();
	});

	let hasDesktopText = $derived(content.text_desktop && !isEmpty(content.text_desktop));
</script>

{#snippet bannerContent()}
	<div class="flex items-center justify-center gap-x-2">
		{#if content.leading_icon?.filename && content.leading_icon.filename.length > 0}
			<Image
				class="h-5 w-5"
				src={content.leading_icon.filename}
				alt={content.leading_icon.alt}
				title={content.leading_icon.title}
			/>
		{/if}
		<span class="max-lg:inline-block" class:lg:hidden={hasDesktopText}
			><SbRichtext text={content.text} /></span
		>
		{#if hasDesktopText}
			<span class="hidden lg:inline-block"><SbRichtext text={content.text_desktop} /></span>
		{/if}
		{#if content.content_cta?.length > 0 && banner}
			<div class="ml-2 max-lg:hidden">
				<Blok content={content.content_cta[0]} />
			</div>
		{/if}
	</div>
{/snippet}

<svelte:element
	this={hasHref ? 'a' : 'div'}
	bind:this={banner}
	use:editable={content}
	href={hasHref ? href : null}
	target={hasHref ? content.target?.target : null}
	role={hasHref ? 'button' : null}
	tabindex="0"
	class:inline-block={hasHref}
	class="mill-banner hidden w-full text-balance p-3 text-center text-[12px]/[160%] antialiased empty:hidden lg:p-2 lg:text-[14px]/[160%] bg-{content.bg_color ||
		'white'} text-{content.text_color || 'brown-1'}"
	onclick={hasHref ? handleClick : null}
>
	{#if hasCountdown}
		<Countdown
			endDate={content.end_date}
			duration={content.countdown_duration}
			text={content.countdown_text}
		>
			{@render bannerContent()}
		</Countdown>
	{:else}
		{@render bannerContent()}
	{/if}
</svelte:element>

<style lang="scss">
	.mill-banner.animate-banner-in {
		animation: banner-in 0.2s ease-out;
	}

	.mill-banner.animate-banner-out {
		animation: banner-out 0.2s ease-out;
	}

	@keyframes banner-in {
		from {
			transform: translateY(-100%);
		}

		to {
			transform: translateY(0);
		}
	}

	@keyframes banner-out {
		from {
			transform: translateY(0);
		}

		to {
			transform: translateY(-100%);
		}
	}
</style>
